/**
 * Progression System
 *
 * Handles player progression mechanics:
 * - XP gain and multipliers
 * - Level up calculations and scaling
 * - Upgrade draft triggering on level up
 * - Visual feedback for level ups
 * - XP scaling per level
 */

// Scene imports
import { showUpgradeDraft } from '../scenes/upgradeDraft.js';

// Configuration imports
import { PROGRESSION_CONFIG } from '../config/constants.js';

// Sound system imports
import { playLevelUp } from './sounds.js';

// Multiplayer imports
import { broadcastLevelUpQueued, isHost } from './multiplayerGame.js';

// Settings import
import { getSetting } from './settings.js';

export function setupProgressionSystem(k, player, reviveAllPlayersCallback = null, isMultiplayer = false) {
    let levelUpInProgress = false;

    // Track pending level ups for multiplayer (queue them for after room clear)
    player.pendingLevelUps = player.pendingLevelUps || [];

    // Add XP to player
    player.addXP = function(amount) {
        if (k.paused || levelUpInProgress) return; // Don't add XP while paused or leveling up

        // Apply XP multiplier if it exists
        const multiplier = player.xpMultiplier || 1;
        const adjustedAmount = Math.floor(amount * multiplier);

        player.xp += adjustedAmount;

        // Check for level up (with safety bounds to prevent infinite loops)
        while (player.xp >= player.xpToNext && !levelUpInProgress && player.xpToNext > 0) {
            levelUpInProgress = true;
            player.xp -= player.xpToNext;
            player.level++;
            const newLevel = player.level;
            // Safety clamp: ensure xpToNext is always at least 1
            player.xpToNext = Math.max(1, Math.floor(player.xpToNext * PROGRESSION_CONFIG.XP_SCALING_FACTOR));

            // Show level up notification and draft directly
            handleLevelUp(k, player, newLevel);
        }
    };

    // Handle level up
    function handleLevelUp(k, player, level) {
        // Don't process if already paused (prevents issues)
        if (k.paused && !isMultiplayer) {
            levelUpInProgress = false;
            return;
        }

        // Play level up sound
        playLevelUp();

        // Note: Revives now only happen on room completion, not on level up

        // Show level up notification
        const notification = k.add([
            k.text(`Level ${level}!`, { size: 24 }),
            k.pos(k.width() / 2, PROGRESSION_CONFIG.LEVEL_UP_NOTIFICATION_Y),
            k.anchor('center'),
            k.color(255, 255, 100),
            k.fixed(),
            k.z(1000) // High z-index to show above other UI
        ]);

        // In multiplayer: queue locally and broadcast to sync with others
        if (isMultiplayer && player.slotIndex !== undefined) {
            // Broadcast to notify everyone
            broadcastLevelUpQueued(player.slotIndex, level);

            // Both host and client queue locally for their own player
            player.pendingLevelUps.push(level);
        } else {
            // Single player: queue locally
            player.pendingLevelUps.push(level);

            // Auto-pause on level up if setting is enabled (single player only)
            const autoPause = getSetting('gameplay', 'autoPause');
            if (autoPause && !k.paused) {
                // Mark that auto-pause is handling this level up
                // This prevents the notification timeout from clearing levelUpInProgress
                const autoPauseHandlingThis = true;

                // Short delay to let the notification show, then pause and show upgrade draft
                k.wait(0.3, () => {
                    // Only proceed if still not paused and we have pending level ups
                    if (!k.paused && player.pendingLevelUps.length > 0) {
                        k.paused = true;
                        levelUpInProgress = true; // Ensure flag is set for upgrade draft
                        // Immediately show upgrade draft
                        const pendingLevel = player.pendingLevelUps.shift();
                        showUpgradeDraft(k, player, () => {
                            levelUpInProgress = false;
                            k.paused = false;
                        }, null, pendingLevel);
                    }
                });

                // Clean up notification but don't touch levelUpInProgress (auto-pause handles it)
                k.wait(PROGRESSION_CONFIG.LEVEL_UP_NOTIFICATION_DURATION, () => {
                    if (notification.exists()) k.destroy(notification);
                    // Don't set levelUpInProgress = false here - auto-pause callback handles it
                });
                return; // Skip the normal notification timeout below
            }
        }

        k.wait(PROGRESSION_CONFIG.LEVEL_UP_NOTIFICATION_DURATION, () => {
            if (notification.exists()) k.destroy(notification);
            levelUpInProgress = false;
        });
    }

    // Return function to manually trigger upgrade selection
    return {
        processPendingLevelUp: () => {
            if (player.pendingLevelUps.length > 0 && !levelUpInProgress) {
                levelUpInProgress = true;
                const level = player.pendingLevelUps.shift(); // Take first pending level
                const playerName = player.playerName || (player.isRemote ? `Player ${player.slotIndex + 1}` : 'You');
                showUpgradeDraft(k, player, () => {
                    levelUpInProgress = false;
                }, isMultiplayer ? playerName : null, level);
            }
        }
    };
}

